import React, { useState, useEffect } from 'react'
import { InputGroup, H5, Spinner, Classes } from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Flex,
  Button,
  Toaster,
  SpaceCard,
  SpaceSelect,
  NewGridTable,
  GridTableStatusEnum,
  IGridTableColumn,
  SpaceCardSkeleton,
} from '@ui'
import api, { INeighborhoodSpace } from '@api'
import { UseNeighborhoodHook } from './hooks/useNeighborhood'
import { pluralize } from '@utils'
import { useReload } from '@hooks/reloadContext'
import { useSelection } from '@hooks'
import useNeighborhoodSpaces from './hooks/useNeighborhoodSpaces'
import { Permissions, PermissionsType, IPermissionsOnNeighborhood } from '@permissions'
import track from '@track'
import Event from '@track/Event'
import { ApolloError } from 'apollo-boost'
import classnames from 'classnames'
import css from './spacesManageSection.module.scss'

const placeholder = <span className={Classes.SKELETON}>placeholder</span>

export default function SpacesManageSection({
  neighborhood,
}: {
  neighborhood: UseNeighborhoodHook
}) {
  const selection = useSelection()
  const reload = useReload()
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<INeighborhoodSpace[]>([])

  const spaces = useNeighborhoodSpaces(neighborhood.id)

  useEffect(() => {
    setWithUndo(spaces.list)
  }, [spaces.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : spaces.query
    ? `${spaces.count} filtered ${pluralize(spaces.count, 'record')}`
    : `${spaces.count} ${pluralize(spaces.count, 'record')}`

  const columnsSpaces: IGridTableColumn<INeighborhoodSpace>[] = [
    {
      label: 'Space',
      Cell: ({ cell }) => {
        return cell ? <SpaceCard space={cell.data} /> : <SpaceCardSkeleton />
      },
    },
    {
      label: 'Department Allocation',
      Cell: ({ cell, skeleton }) => {
        return cell ? (
          <div>
            {cell.data.spaceAssignment?.department
              ? `${cell.data.spaceAssignment.department.name}${
                  cell.data.spaceAssignment?.team ? ' | ' + cell.data.spaceAssignment.team.name : ''
                }`
              : '-'}
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            place
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell || !isHighlighted) return null

        const { data: space } = cell

        return (
          <Permissions
            type={PermissionsType.NEIGHBORHOOD}
            on={neighborhood.item ? [neighborhood.item] : null}
          >
            {(permissions) => {
              const { canRemoveSpaces } = permissions as IPermissionsOnNeighborhood

              return canRemoveSpaces ? (
                <div className="w-full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    round
                    minimal
                    className="remove-icon"
                    iconName="times"
                    tooltip={`Remove ${space.externalId}`}
                    style={{ border: 'none' }}
                    onClick={() => {
                      const removedSpace = space

                      setWithUndo((prev) => [...prev.filter((i) => space.id !== i.id)])
                      selection.clear()

                      api.neighborhood
                        .update({
                          id: neighborhood.id,
                          removeSpaces: [{ id: space.id }],
                        })
                        .then(() => {
                          Toaster.success({
                            action: {
                              onClick: () => {
                                api.neighborhood.update({
                                  id: neighborhood.id,
                                  spaces: [{ id: space.id }],
                                })

                                setWithUndo((prev) => [removedSpace, ...prev])

                                track.neighborhoodDialog({
                                  eventName: Event.UNDO_CLICK,
                                  props: {
                                    component: 'Space Manage',
                                    actionName: 'remove',
                                  },
                                })
                              },
                              text: 'Undo',
                            },
                            message: 'Space removed from Neighborhood',
                          })
                        })
                        .then(() => reload.neighborhoods())
                        .catch((error) => {
                          Toaster.dismissAll()
                          Toaster.fail({ error })
                          reload.neighborhoods()
                        })

                      track.neighborhoodDialog({
                        eventName: Event.RECORD_REMOVE,
                        props: {
                          component: 'Space Manage',
                        },
                      })
                    }}
                  />
                </div>
              ) : null
            }}
          </Permissions>
        )
      },
    },
  ]

  const formatItems = (items: INeighborhoodSpace[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(spaces.list)

  return (
    <Permissions
      type={PermissionsType.NEIGHBORHOOD}
      on={neighborhood.item ? [neighborhood.item] : null}
    >
      {(permissions) => {
        const { canAddSpaces, canRemoveSpaces } = permissions as IPermissionsOnNeighborhood

        return (
          <>
            <Flex
              align="center"
              justify="space-between"
              style={{
                marginTop: 40,
                height: 40,
              }}
            >
              <Flex align="center">
                <H5
                  style={{
                    marginBottom: 0,
                    marginRight: 6,
                    lineHeight: '30px',
                  }}
                >
                  Spaces
                </H5>
                {showSearch ? (
                  <InputGroup
                    autoFocus
                    leftIcon="search"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      spaces.run(e.target.value)
                    }}
                    placeholder="Search spaces..."
                    rightElement={
                      <RightElement
                        showWithoutQuery
                        query={spaces.query}
                        onClear={() => {
                          if (spaces.query) spaces.run('')
                          setShowSearch(false)
                        }}
                        isQueryResultLoading={spaces.isLoading}
                      />
                    }
                    value={spaces.query}
                  />
                ) : (
                  <IconButton round minimal iconName="search" onClick={() => setShowSearch(true)} />
                )}
              </Flex>
            </Flex>
            <Flex justify="space-between">
              <Flex align="center">
                {Boolean(selection.count) && (
                  <span style={{ paddingRight: 10 }}>
                    <Confirmation
                      shouldHide={disableConfirmation}
                      enabled={!disableConfirmation}
                      text={`Remove ${pluralize(selection.count, 'space')} from neighborhood?`}
                      onConfirm={() => {
                        const spacesForDelete = withUndo.filter((i) =>
                          Object.keys(selection.items).includes(i.id)
                        )

                        const spacesIds = Object.entries(selection.items)
                          .filter(([_, value]) => Boolean(value))
                          .map(([id]) => id)

                        setWithUndo((prev) => [...prev.filter((i) => !spacesIds.includes(i.id))])
                        selection.clear()

                        track.neighborhoodDialog({
                          eventName: Event.BATCH_ACTION,
                          props: {
                            actionName: 'Delete spaces',
                            actionValue: null,
                            batchCount: spacesIds.length,
                          },
                        })

                        api.neighborhood
                          .update({
                            id: neighborhood.id,
                            removeSpaces: spacesIds.map((space) => ({
                              id: space,
                            })),
                          })
                          .then(() => {
                            Toaster.success({
                              action: {
                                onClick: () => {
                                  setWithUndo((prev) => [...spacesForDelete, ...prev])
                                  setDisableConfirmation(false)

                                  api.neighborhood
                                    .update({
                                      id: neighborhood.id,
                                      spaces: spacesForDelete.map(({ id }) => ({
                                        id,
                                      })),
                                    })
                                    .then(() => reload.neighborhoods())
                                    .catch((error: ApolloError) => Toaster.fail({ error }))

                                  track.neighborhoodDialog({
                                    eventName: Event.UNDO_CLICK,
                                    props: {
                                      component: 'Space Manage',
                                      actionName: 'remove',
                                    },
                                  })
                                },
                                text: 'Undo',
                              },
                              message: `${pluralize(
                                selection.count,
                                'Space'
                              )} removed from Neighborhood`,
                            })
                          })
                          .then(() =>
                            setWithUndo((prev) => [
                              ...prev.filter((i) => !spacesIds.includes(i.id)),
                            ])
                          )
                          .then(() => reload.neighborhoods())
                          .catch((error) => {
                            Toaster.dismissAll()
                            Toaster.fail({ error })
                            reload.neighborhoods()
                          })
                      }}
                    >
                      <Button text="Remove" />
                    </Confirmation>
                  </span>
                )}
                <Flex align="baseline">
                  {!spaces.isLoading && <span style={{ color: 'var(--grey1)' }}>{helperText}</span>}
                  {spaces.isLoading && placeholder}
                  {Boolean(selection.count) && (
                    <IconButton
                      minimal
                      tooltip="Clear selection"
                      iconName="times"
                      style={{ marginLeft: 4 }}
                      data-button="clear_selection"
                      onClick={() => {
                        selection.clear()

                        track.neighborhoodDialog({
                          eventName: Event.CLEAR_SELECTION,
                          props: {
                            component: 'Space Manage',
                          },
                        })
                      }}
                    />
                  )}
                </Flex>
              </Flex>
              {canAddSpaces ? (
                <SpaceSelect
                  key={neighborhood.item?.floor.id}
                  buttonProps={{ iconName: 'plus' }}
                  placeholder="Space"
                  showSeatableAndAllocatable
                  value={null}
                  floorId={neighborhood.item?.floor.id}
                  onChange={async (space) => {
                    if (space) {
                      try {
                        await api.space
                          .batchUpdateUsageTypes([space.id], 'neighborhood')
                          .catch((e: ApolloError) => e)

                        await api.neighborhood.update({
                          id: neighborhood.id,
                          spaces: [...spaces.list.map(({ id }) => ({ id })), { id: space.id }],
                        })

                        Toaster.success({
                          message: 'Space added to Neighborhood',
                        })

                        reload.neighborhoods()
                      } catch (error: any) {
                        Toaster.dismissAll()
                        Toaster.fail({ error })
                        reload.neighborhoods()
                      }
                    }

                    track.neighborhoodDialog({
                      eventName: Event.RECORD_ADD,
                      props: {
                        component: 'Space Manage',
                      },
                    })
                  }}
                />
              ) : null}
            </Flex>
            <div className={css.table_wrapper}>
              <NewGridTable<INeighborhoodSpace>
                status={spaces.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
                noDataMessage={spaces.query ? undefined : 'No Spaces associated yet'}
                customColumnWidths={['auto', 'auto', '50px']}
                sticky={false}
                columns={columnsSpaces}
                selection={canRemoveSpaces ? selection : undefined}
                data={formatedItems}
                skeletonSize={3}
              />
            </div>
            {spaces.list && spaces.pageInfo.hasNextPage && (
              <Button
                minimal
                style={{ marginTop: 5, width: 100 }}
                text={`View ${
                  spaces.count - spaces.list.length > 5 ? '5' : spaces.count - spaces.list.length
                } more`}
                onClick={() => {
                  spaces.loadMore()

                  track.neighborhoodDialog({
                    eventName: Event.PAGE,
                    props: {
                      component: 'Space Manage',
                    },
                  })
                }}
              />
            )}
          </>
        )
      }}
    </Permissions>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear?.()

          track.neighborhoodDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              filterName: 'Query',
              filterValue: null,
              component: 'Space Manage',
            },
          })
        }}
      />
    )
  return null
}
